import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { doctorDisplayName } from './doctorColors';
import { statusColor, statusLabel, procedureLabel } from './appointmentStatus';
import { useAppointmentsCalendarQuery } from '../../api/appointmentsApi';

const TABS = [
  { key: 'all',     label: 'All' },
  { key: 'online',  label: 'Online' },
  { key: 'offline', label: 'Offline' },
];

function fmtTime(d) {
  return d.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' });
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function isSameDay(a, b) {
  return a.toDateString() === b.toDateString();
}

export default function TodayScheduleSidebar({
  appointments = [],
  onAppointmentClick,
  onAddWalkin,
}) {
  const [tab, setTab] = useState('all');
  // Status filter driven by the stat tiles: '' (all) | 'wait' | 'engaged' | 'done'.
  const [statusFilter, setStatusFilter] = useState('');
  const toggleStatus = (key) => setStatusFilter((cur) => (cur === key ? '' : key));
  const matchesStatus = (a) => {
    if (statusFilter === 'wait') return a.status === 'Scheduled' || a.status === 'Confirmed';
    if (statusFilter === 'engaged') return a.status === 'InProgress';
    if (statusFilter === 'done') return a.status === 'Completed';
    return true;
  };

  // The visible day inside the sidebar — defaults to today, but the arrows can move it forward/back.
  const [viewDate, setViewDate] = useState(() => startOfDay(new Date()));
  const today = useMemo(() => startOfDay(new Date()), []);
  const isToday = isSameDay(viewDate, today);

  const dayStart = useMemo(() => startOfDay(viewDate), [viewDate]);
  const dayEnd = useMemo(() => {
    const t = new Date(dayStart);
    t.setDate(t.getDate() + 1);
    return t;
  }, [dayStart]);

  const stepBack    = () => { const d = new Date(viewDate); d.setDate(d.getDate() - 1); setViewDate(d); };
  const stepForward = () => { const d = new Date(viewDate); d.setDate(d.getDate() + 1); setViewDate(d); };
  const goToday     = () => setViewDate(startOfDay(new Date()));

  // If the visible day is outside the appointments[] window passed in (e.g. user clicked
  // forward into next month), fall back to a dedicated calendar query for that day.
  const propsHaveDay = useMemo(() => {
    if (!appointments?.length) return false;
    // Cheap heuristic — if any appointment lands inside the visible day, the parent already covers it.
    return appointments.some((a) => {
      const d = new Date(a.scheduledAt);
      return d >= dayStart && d < dayEnd;
    }) || isSameDay(viewDate, today);
  }, [appointments, dayStart, dayEnd, viewDate, today]);

  const { data: fetchedDay = [] } = useAppointmentsCalendarQuery(
    { from: dayStart.toISOString(), to: dayEnd.toISOString() },
    { skip: propsHaveDay, refetchOnMountOrArgChange: 30 },
  );

  const sourceForDay = propsHaveDay ? appointments : fetchedDay;

  const visibleAppts = useMemo(() => {
    return sourceForDay
      .filter((a) => {
        const d = new Date(a.scheduledAt);
        if (d < dayStart || d >= dayEnd) return false;
        if (!matchesStatus(a)) return false;
        if (tab === 'online')  return a.type === 'Online'  || a.mode === 'Online'  || a.isOnline === true;
        if (tab === 'offline') return !(a.type === 'Online' || a.mode === 'Online' || a.isOnline === true);
        return true;
      })
      .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceForDay, dayStart, dayEnd, tab, statusFilter]);

  // Stats always count ALL of the visible day's appointments — independent of the tab filter
  const allOnDay = useMemo(() => {
    return sourceForDay.filter((a) => {
      const d = new Date(a.scheduledAt);
      return d >= dayStart && d < dayEnd;
    });
  }, [sourceForDay, dayStart, dayEnd]);

  const stats = useMemo(() => ({
    today:   allOnDay.length,
    wait:    allOnDay.filter((a) => a.status === 'Scheduled' || a.status === 'Confirmed').length,
    engaged: allOnDay.filter((a) => a.status === 'InProgress').length,
    done:    allOnDay.filter((a) => a.status === 'Completed').length,
  }), [allOnDay]);

  // Header title: "Today's Schedule" when on today, otherwise show the actual date.
  const headerTitle = isToday
    ? "Today's Schedule"
    : viewDate.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', day: '2-digit', month: 'short' });
  const headerSub = isToday
    ? viewDate.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', day: '2-digit', month: 'short' })
    : null;

  return (
    <aside className="cal-right">
      <div className="cal-right-header">
        <div className="cal-right-header-title">
          <span>{headerTitle}</span>
          {headerSub && <span className="cal-right-header-sub">{headerSub}</span>}
          {!isToday && (
            <button
              type="button"
              className="cal-right-today-link"
              onClick={goToday}
              title="Jump back to today"
            >
              Today
            </button>
          )}
        </div>
        <div className="cal-right-header-nav">
          <button className="cal-header-icon" aria-label="Previous day" onClick={stepBack}><ChevronLeft size={14} /></button>
          <button className="cal-header-icon" aria-label="Next day"     onClick={stepForward}><ChevronRight size={14} /></button>
        </div>
      </div>

      <button className="cal-walkin" onClick={onAddWalkin}>
        Add Walk-in Appointment
      </button>

      <div className="cal-right-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`cal-right-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="cal-right-stats">
        <button
          type="button"
          className={`cal-right-stat stat-today ${statusFilter === '' ? 'active' : ''}`}
          aria-pressed={statusFilter === ''}
          onClick={() => setStatusFilter('')}
          title="Show all appointments for the day"
        >
          <div className="cal-right-stat-label">{isToday ? 'Today' : 'Day'}</div>
          <div className="cal-right-stat-value">{stats.today}</div>
        </button>
        <button
          type="button"
          className={`cal-right-stat stat-wait ${statusFilter === 'wait' ? 'active' : ''}`}
          aria-pressed={statusFilter === 'wait'}
          onClick={() => toggleStatus('wait')}
          title="Filter to waiting (scheduled/confirmed)"
        >
          <div className="cal-right-stat-label">Waiting</div>
          <div className="cal-right-stat-value">{stats.wait}</div>
        </button>
        <button
          type="button"
          className={`cal-right-stat stat-engaged ${statusFilter === 'engaged' ? 'active' : ''}`}
          aria-pressed={statusFilter === 'engaged'}
          onClick={() => toggleStatus('engaged')}
          title="Filter to engaged (in progress)"
        >
          <div className="cal-right-stat-label">Engaged</div>
          <div className="cal-right-stat-value">{stats.engaged}</div>
        </button>
        <button
          type="button"
          className={`cal-right-stat stat-done ${statusFilter === 'done' ? 'active' : ''}`}
          aria-pressed={statusFilter === 'done'}
          onClick={() => toggleStatus('done')}
          title="Filter to done (checked out)"
        >
          <div className="cal-right-stat-label">Done</div>
          <div className="cal-right-stat-value">{stats.done}</div>
        </button>
      </div>

      <div className="cal-right-list">
        {visibleAppts.map((a) => {
          const time = fmtTime(new Date(a.scheduledAt));
          const createdMs = a.createdAt ? new Date(a.createdAt).getTime() : null;
          const isNew = createdMs != null
            && Date.now() - createdMs >= 0
            && Date.now() - createdMs < 1000 * 60 * 60 * 24;
          const dotColor = statusColor(a.status);
          return (
            <div key={a.id} className="cal-appt-row" onClick={() => onAppointmentClick(a)}>
              <div className="cal-appt-time-col">{time}</div>
              <div className="cal-appt-body">
                <div className="cal-appt-body-name">
                  {a.patientName ?? a.leadName ?? 'Patient'}
                  {isNew && <span className="cal-new-badge">N</span>}
                </div>
                {/* `a.reason` was read here but AppointmentDto has no such field (Reason exists only
                    on the reschedule/status/reassign REQUESTS), so this line could only ever render
                    the placeholder. The procedure is the real answer to "what is this visit". */}
                <div className="cal-appt-body-reason">{procedureLabel(a) ?? 'Session type not set'}</div>
                <div className="cal-appt-body-doc">{a.assignedUserName ? doctorDisplayName(a.assignedUserName) : '—'}</div>
              </div>
              <div className="cal-appt-status-dot" style={{ background: dotColor }} title={statusLabel(a.status)} />
            </div>
          );
        })}
        {visibleAppts.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.83rem' }}>
            {isToday ? 'No appointments today.' : 'No appointments on this day.'}
          </div>
        )}
      </div>
    </aside>
  );
}
